import React from 'react'
import {
  Action,
  ActionHandler,
  HandleClick,
  ModalContext,
  LinkTarget
} from '../shoplist-app/shopping/types/ShoppingPageType'
import { handleGoldStoreTermsModal, handleAction } from './actionHandlers'
export interface GetActionProps {
  action?: Action
  modalContext?: ModalContext
  feedbackModalHandler?: ActionHandler
  privacyDisclosureModalHandler?: ActionHandler
  dismissHandler?: ActionHandler
  onClick?: HandleClick
}

type GetLinkTarget = (
  navigationType: Action['navigationType']
) => LinkTarget | undefined
type IsLink = (
  action: Action['type'],
  navigationType: Action['navigationType']
) => boolean
type GetModalTypes = (
  action: Action['type'],
  navigationType: Action['navigationType']
) => {
  isDismiss: boolean
  isFeedbackModal: boolean
  isGoldStoreTermsModal: boolean
  isPrivacyDisclosureModal: boolean
}

const getLinkTarget: GetLinkTarget = navigationType => {
  if (navigationType === 'NEW_TAB') return '_blank'
  if (navigationType === 'SAME_TAB') return '_self'
  return undefined
}

export const isLink: IsLink = (type, navigationType) => {
  if (type !== 'NAVIGATION') return false
  return navigationType === 'NEW_TAB' || navigationType === 'SAME_TAB'
}

const getModalTypes: GetModalTypes = (type, navigationType) => {
  const isNavigation = type === 'NAVIGATION'
  const isGoldStoreTermsModal = isNavigation && navigationType === 'GOLD_MODAL'
  const isDismiss = type === 'DISMISS'
  const isFeedbackModal = isNavigation && !navigationType
  const isPrivacyDisclosureModal = type === 'MODAL'

  return {
    isDismiss,
    isFeedbackModal,
    isGoldStoreTermsModal,
    isPrivacyDisclosureModal
  }
}

// TODO: feedbackModalHandler and privacyDisclosureModalHandler should be handled inside and not be received as props.
const getActionProps = ({
  action,
  modalContext,
  feedbackModalHandler,
  privacyDisclosureModalHandler,
  dismissHandler,
  onClick
}: GetActionProps) => {
  action = action || {}
  const { dispatch: modalDispatch } = modalContext || {}
  const { type, title, navigationTarget, navigationType } = action
  const target = getLinkTarget(navigationType)
  const href = isLink(type, navigationType) ? navigationTarget : undefined
  const {
    isDismiss,
    isFeedbackModal,
    isGoldStoreTermsModal,
    isPrivacyDisclosureModal
  } = getModalTypes(type, navigationType)
  const isButton =
    isGoldStoreTermsModal ||
    isFeedbackModal ||
    isPrivacyDisclosureModal ||
    isDismiss

  const handleLinkClick: HandleClick = event => onClick && onClick(event)

  const handleButtonClick: HandleClick = event => {
    switch (true) {
      case isGoldStoreTermsModal:
        handleGoldStoreTermsModal({ modalDispatch, navigationTarget })
        break
      case isFeedbackModal:
        handleAction({ actionHandler: feedbackModalHandler })
        break
      case isPrivacyDisclosureModal:
        handleAction({ actionHandler: privacyDisclosureModalHandler })
        break
      case isDismiss:
        handleAction({ actionHandler: dismissHandler })
        break
      default:
        break
    }

    if (onClick) {
      onClick(event)
    }
  }

  const handleButtonKeyPress: HandleClick<React.KeyboardEvent> = event => {
    if (!event) {
      return
    }

    if ((event.key === ' ' || event.key === 'Enter') && isButton) {
      event.preventDefault()
      handleButtonClick(event)
    }
  }

  return {
    title,
    isLink: isLink(type, navigationType),
    href,
    target,
    isButton,
    handleLinkClick,
    handleButtonClick,
    handleButtonKeyPress
  }
}

/**
 * title: text content for element.
 * isLink: if true element should be rendered as <a> tag.
 * href: href for <a> element in case isLink is true.
 * target: target for <a> element in case isLink is true.
 * handleLinkClick: onClick handler for <a> element in case isLink is true.
 * isButton: element should be redered as <button> or similar (role="button") in case isButton is true.
 * handleButtonClick: onClick handler for element in case isButton is true.
 * handleButtonKeyPress: handler to simulate space bar or enter key press on element (natively button triggers on click event on spacebar or enter key press) in case isButton is true and element is not <button>.
 */
export { getActionProps }
